/**
 * As abas da mesa única (E205.F1.T04, ADR-0147 §1): Exame · Pendências · Documentos · Fotografias e
 * facial · Conclusão, com o `Tabs` do design system (Radix), uma visível por vez.
 *
 * Os painéis ficam montados e só se escondem (`forceMount` + `hidden`): a área do material lê o caso,
 * dispara o processamento automático e relê a mesa, e o card do pacote segue ouvindo a mudança do
 * pacote, estejam ou não à vista. Desmontar a aba escondida pararia essas leituras.
 *
 * O painel de uma aba pode morar em qualquer componente de dentro das abas (`PainelDaAba`): a área
 * do material desenha os painéis de Documentos e de Fotografias. Fora das abas (a área sozinha, nos
 * testes dela), o painel é um bloco comum, sempre à vista.
 */
import { createContext, useContext, type ReactNode } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { ABAS_DA_MESA, ATRIBUTO_DA_ABA, eAba, type AbaDaMesa } from '@/lib/mesaEmAbas';

const t = ptBR.mesaUnica.abas;

const AbaAtivaContexto = createContext<AbaDaMesa | null>(null);

/** O que vai ao lado do nome da aba (o contador de Pendências). */
export type MarcasDasAbas = Partial<Record<AbaDaMesa, ReactNode>>;

export interface AbasDaMesaProps {
  aba: AbaDaMesa;
  onAba: (aba: AbaDaMesa) => void;
  marcas?: MarcasDasAbas;
  children: ReactNode;
}

export function AbasDaMesa({ aba, onAba, marcas = {}, children }: AbasDaMesaProps): JSX.Element {
  return (
    <Tabs
      value={aba}
      onValueChange={(valor) => {
        if (eAba(valor)) onAba(valor);
      }}
      className="mt-sc-6"
      data-testid="mesa-abas"
    >
      <TabsList
        aria-label={t.rotulo}
        className="flex w-full justify-start gap-sc-1 overflow-x-auto bg-transparent"
      >
        {ABAS_DA_MESA.map((a) => (
          <TabsTrigger
            key={a}
            value={a}
            className="-mb-px border-b-2 border-transparent data-[state=active]:border-accent-700"
            data-testid={`mesa-aba-${a}`}
          >
            {t[a]}
            {marcas[a] ?? null}
          </TabsTrigger>
        ))}
      </TabsList>
      <AbaAtivaContexto.Provider value={aba}>{children}</AbaAtivaContexto.Provider>
    </Tabs>
  );
}

export interface PainelDaAbaProps {
  aba: AbaDaMesa;
  children: ReactNode;
  className?: string;
}

export function PainelDaAba({ aba, children, className }: PainelDaAbaProps): JSX.Element {
  const ativa = useContext(AbaAtivaContexto);
  const marca = { [ATRIBUTO_DA_ABA]: aba };
  if (ativa === null) {
    return (
      <div className={className} {...marca}>
        {children}
      </div>
    );
  }
  return (
    <TabsContent
      value={aba}
      forceMount
      hidden={ativa !== aba}
      className={className ?? 'mt-sc-4'}
      data-testid={`mesa-painel-${aba}`}
      {...marca}
    >
      {children}
    </TabsContent>
  );
}

/** E205.F3: o número de pendências ao lado do nome da aba; sem pendência (ou sem leitura), nada. */
export function ContadorDePendencias({ n }: { n: number | null }): JSX.Element | null {
  if (n === null || n === 0) return null;
  return (
    <span className="text-fg-muted" data-testid="mesa-aba-pendencias-contador">
      <span aria-hidden="true">{ptBR.mesaUnica.pendencias.contador.replace('{n}', String(n))}</span>
      <span className="sr-only">
        {ptBR.mesaUnica.pendencias.contadorRotulo.replace('{n}', String(n))}
      </span>
    </span>
  );
}
